/**
 * StartPlanPromptModal - Apple-style frosted glass prompt.
 *
 * Two modes:
 * - "quiz": customer has no subscription data at all. Non-dismissible hard
 *   gate — starting the quiz is the only way forward.
 * - "resume": customer has bundle subscription(s) but none active
 *   (paused/cancelled). Two-step flow: (1) overview of what comes back,
 *   (2) pick the preferred delivery date, then reactivate. Dismissible, and
 *   the dismissal is remembered for the session.
 */

import React, { useState } from "react";
import { Box, Typography, Modal, Fade, Button, ButtonBase, CircularProgress } from "@mui/material";
import PetsIcon from "@mui/icons-material/Pets";
import ReplayIcon from "@mui/icons-material/Replay";
import EventIcon from "@mui/icons-material/Event";
import { QuizButton } from "./shared/QuizButton";
import { QUIZ } from "@/theme/quizTokens";

const DISMISS_KEY = "paws_resume_prompt_dismissed";

export function wasResumePromptDismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

export function markResumePromptDismissed() {
  try {
    sessionStorage.setItem(DISMISS_KEY, "1");
  } catch {}
}

export function clearResumePromptDismissal() {
  try {
    sessionStorage.removeItem(DISMISS_KEY);
  } catch {}
}

export type PausedPlanOverview = {
  dogName: string | null;
  planDays: number | null;
  packSizeGrams: number | null;
  items: { title: string; imageUrl: string | null; count: number }[];
};

export type ResumeDateOption = {
  date: string; // YYYY-MM-DD
  dayOfWeek?: string;
};

// "Fri 3 July" from YYYY-MM-DD, pinned to UTC
function formatDateLabel(ymd: string): string {
  const date = new Date(ymd.slice(0, 10) + "T00:00:00Z");
  if (Number.isNaN(date.getTime())) return ymd;
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

export function StartPlanPromptModal({
  open,
  mode,
  pausedPlans = [],
  onLoadDates,
  onResume,
  onClose,
}: {
  open: boolean;
  mode: "quiz" | "resume";
  /** resume mode: what the customer paused, shown so they know what comes back */
  pausedPlans?: PausedPlanOverview[];
  /** resume mode: fetch available delivery dates for step 2 */
  onLoadDates?: () => Promise<{ ok: boolean; availableDates: ResumeDateOption[]; error?: string }>;
  /** resume mode: reactivate; deliveryDate is the customer's pick (null = keep default schedule) */
  onResume?: (deliveryDate: string | null) => Promise<{ ok: boolean; error?: string }>;
  /** resume mode only: dismiss the prompt */
  onClose?: () => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [dates, setDates] = useState<ResumeDateOption[]>([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [datesError, setDatesError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);

  const isResume = mode === "resume";

  const handleClose = () => {
    setStep(1);
    setResumeError(null);
    onClose?.();
  };

  const goToDateStep = async () => {
    setStep(2);
    setResumeError(null);
    if (!onLoadDates || dates.length > 0) return;
    setDatesLoading(true);
    setDatesError(null);
    try {
      const result = await onLoadDates();
      if (result.ok && result.availableDates.length > 0) {
        setDates(result.availableDates);
        setSelectedDate(result.availableDates[0]?.date ?? null);
      } else {
        setDatesError(result.error || "We couldn't load delivery dates.");
      }
    } catch {
      setDatesError("We couldn't load delivery dates.");
    }
    setDatesLoading(false);
  };

  const handleResume = async () => {
    if (!onResume || resuming) return;
    setResuming(true);
    setResumeError(null);
    const result = await onResume(selectedDate);
    setResuming(false);
    if (!result.ok) {
      setResumeError(
        result.error || "We couldn't resume your plan. Please try again or contact support."
      );
    }
  };

  const title = isResume
    ? step === 1
      ? "Welcome back!"
      : "Pick your delivery day"
    : "Let's fill your dog's bowl";

  const body = isResume
    ? step === 1
      ? pausedPlans.length > 0
        ? "Your plan is currently paused. Here's what's waiting to come back when you resume:"
        : "Your plan is currently paused. Resume it and your deliveries pick up right where they left off."
      : "When would you like your first delivery back?"
    : "You don't have a meal plan yet. Take our quick quiz and we'll build one around your dog, ready to order in minutes.";

  return (
    <Modal
      open={open}
      onClose={isResume ? handleClose : undefined}
      closeAfterTransition
      sx={{ zIndex: 60000 }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(51, 33, 21, 0.28)",
            backdropFilter: "blur(18px) saturate(160%)",
            WebkitBackdropFilter: "blur(18px) saturate(160%)",
          },
        },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(400px, calc(100vw - 32px))",
            maxHeight: "min(640px, calc(100vh - 48px))",
            overflowY: "auto",
            outline: "none",
            borderRadius: "28px",
            background: "rgba(255, 253, 248, 0.88)",
            backdropFilter: "blur(30px) saturate(180%)",
            WebkitBackdropFilter: "blur(30px) saturate(180%)",
            border: "1px solid rgba(255,255,255,0.6)",
            boxShadow:
              "0 24px 70px rgba(51, 33, 21, 0.28), 0 2px 8px rgba(51, 33, 21, 0.08)",
            p: { xs: 3, sm: 4 },
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              mx: "auto",
              mb: 2,
              borderRadius: "20px",
              display: "grid",
              placeItems: "center",
              background: "linear-gradient(145deg, #4A3026, #332115)",
              boxShadow: "0 10px 24px rgba(74, 48, 38, 0.35)",
            }}
          >
            {!isResume ? (
              <PetsIcon sx={{ color: QUIZ.color.white, fontSize: 32 }} />
            ) : step === 1 ? (
              <ReplayIcon sx={{ color: QUIZ.color.white, fontSize: 32 }} />
            ) : (
              <EventIcon sx={{ color: QUIZ.color.white, fontSize: 32 }} />
            )}
          </Box>

          <Typography
            sx={{
              fontSize: { xs: 20, sm: 22 },
              fontWeight: 800,
              color: QUIZ.color.ink,
              letterSpacing: "-0.3px",
              mb: 1,
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              fontSize: 14.5,
              lineHeight: 1.55,
              color: "rgba(51, 33, 21, 0.65)",
              fontWeight: 500,
              mb: 3,
            }}
          >
            {body}
          </Typography>

          {/* Step 1: what's coming back */}
          {isResume && step === 1 && pausedPlans.length > 0 && (
            <Box sx={{ mb: 3, display: "grid", gap: 1.25 }}>
              {pausedPlans.map((plan, pi) => (
                <Box
                  key={pi}
                  sx={{
                    background: "rgba(255, 255, 255, 0.7)",
                    border: "1px solid rgba(51, 33, 21, 0.1)",
                    borderRadius: "16px",
                    p: 1.75,
                    textAlign: "left",
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: QUIZ.color.ink, mb: 1 }}>
                    {plan.dogName ? `${plan.dogName}'s plan` : "Your plan"}
                    {plan.planDays ? ` · every ${plan.planDays} days` : ""}
                  </Typography>
                  <Box sx={{ display: "grid", gap: 0.75 }}>
                    {plan.items.map((item) => (
                      <Box key={item.title} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: "8px",
                            overflow: "hidden",
                            background: "rgba(51, 33, 21, 0.06)",
                            flexShrink: 0,
                          }}
                        >
                          {item.imageUrl && (
                            <Box
                              component="img"
                              src={item.imageUrl}
                              alt=""
                              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "rgba(51, 33, 21, 0.8)",
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: QUIZ.color.ink, flexShrink: 0 }}>
                          ×{item.count}
                          {plan.packSizeGrams ? ` · ${plan.packSizeGrams}g` : ""}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Step 2: delivery date picker */}
          {isResume && step === 2 && (
            <Box sx={{ mb: 3 }}>
              {datesLoading ? (
                <CircularProgress size={28} sx={{ color: QUIZ.color.brown, my: 2 }} />
              ) : datesError ? (
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: QUIZ.color.grey,
                    background: "rgba(51, 33, 21, 0.05)",
                    borderRadius: "12px",
                    px: 2,
                    py: 1.25,
                  }}
                >
                  {datesError} You can still resume — we'll use your usual schedule.
                </Typography>
              ) : (
                <Box sx={{ display: "grid", gap: 0.75, maxHeight: 240, overflowY: "auto", pr: 0.5 }}>
                  {dates.map((d) => {
                    const selected = selectedDate === d.date;
                    return (
                      <ButtonBase
                        key={d.date}
                        onClick={() => setSelectedDate(d.date)}
                        sx={{
                          borderRadius: "12px",
                          px: 2,
                          py: 1.25,
                          justifyContent: "space-between",
                          display: "flex",
                          background: selected ? QUIZ.color.blue : "rgba(255, 255, 255, 0.7)",
                          border: selected
                            ? "1px solid transparent"
                            : "1px solid rgba(51, 33, 21, 0.12)",
                          transition: "background 120ms ease",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 14.5, fontWeight: selected ? 800 : 600, color: QUIZ.color.ink }}
                        >
                          {formatDateLabel(d.date)}
                        </Typography>
                        {selected && (
                          <Typography sx={{ fontSize: 12, fontWeight: 800, color: QUIZ.color.ink }}>
                            First delivery
                          </Typography>
                        )}
                      </ButtonBase>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          {resumeError && (
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#c62828",
                background: "rgba(198, 40, 40, 0.07)",
                border: "1px solid rgba(198, 40, 40, 0.2)",
                borderRadius: "12px",
                px: 2,
                py: 1.25,
                mb: 2,
                textAlign: "left",
              }}
            >
              {resumeError}
            </Typography>
          )}

          {/* Actions */}
          {!isResume ? (
            <QuizButton
              variant="primary"
              href="/pages/quiz-1"
              fullWidth
              sx={{ borderRadius: "14px", py: 1.4, fontSize: 16 }}
            >
              Start the quiz
            </QuizButton>
          ) : step === 1 ? (
            <>
              <QuizButton
                variant="primary"
                onClick={goToDateStep}
                fullWidth
                sx={{ borderRadius: "14px", py: 1.4, fontSize: 16 }}
              >
                Resume my plan
              </QuizButton>
              <Button
                onClick={handleClose}
                fullWidth
                sx={{
                  mt: 1,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "rgba(51, 33, 21, 0.55)",
                  borderRadius: "14px",
                  "&:hover": { background: "rgba(51, 33, 21, 0.05)" },
                }}
              >
                Maybe later
              </Button>
            </>
          ) : (
            <>
              <QuizButton
                variant="primary"
                onClick={handleResume}
                disabled={resuming || datesLoading}
                fullWidth
                sx={{ borderRadius: "14px", py: 1.4, fontSize: 16 }}
              >
                {resuming ? (
                  <CircularProgress size={22} sx={{ color: QUIZ.color.white }} />
                ) : selectedDate ? (
                  `Resume · first delivery ${formatDateLabel(selectedDate)}`
                ) : (
                  "Resume my plan"
                )}
              </QuizButton>
              <Button
                onClick={() => setStep(1)}
                disabled={resuming}
                fullWidth
                sx={{
                  mt: 1,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "rgba(51, 33, 21, 0.55)",
                  borderRadius: "14px",
                  "&:hover": { background: "rgba(51, 33, 21, 0.05)" },
                }}
              >
                Back
              </Button>
            </>
          )}
        </Box>
      </Fade>
    </Modal>
  );
}
